import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { apiErrorText, useListGroupRatesQuery, type GroupRate } from '@shared/api';
import { Drawer, DrawerBody, DrawerCloseButton, DrawerHeader } from '@shared/ui/Drawer';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  GroupUnitChip,
  Notice,
  RateKindChip,
  TabPanel,
  Tabs,
  TextButton,
  fleetLeading,
  fleetType,
  formatIsoDate,
  formatMoney,
  openRate,
  rateUnit,
  tabId,
  tabPanelId,
  typo,
  type RateRow,
  type TabItem,
} from '@entities/fleet';
import { RateForm } from './RateForm';
import { RateHistory } from './RateHistory';
import { targetKey, targetLabel, type RateTarget } from './scope';

export type RateView = 'new' | 'history';

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 36px;
`;

const Eyebrow = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const Title = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  font-size: ${fleetType.title};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  letter-spacing: -0.01em;
  text-wrap: balance;
  overflow-wrap: anywhere;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const MetaRow = styled.div<{ $last: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(2.5)};
  padding-bottom: ${({ theme, $last }) => ($last ? theme.spacing(4) : 0)};

  & > .text {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }
`;

const Flash = styled(Notice)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

/**
 * Вкладка с формой: внутри — прокручиваемое тело и подвал панели, поэтому
 * сама вкладка — колонка во всю оставшуюся высоту.
 */
const FormTab = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;

/** История — вкладка, только когда вкладки есть (у читателя их нет). */
function MaybeTabPanel({
  tabbed,
  idPrefix,
  children,
}: {
  tabbed: boolean;
  idPrefix: string;
  children: ReactNode;
}) {
  return tabbed ? (
    <TabPanel idPrefix={idPrefix} id="history">
      {children}
    </TabPanel>
  ) : (
    <>{children}</>
  );
}

function Skeleton() {
  return (
    <DrawerBody aria-busy="true">
      <SkeletonLine $height={48} />
      <SkeletonLine $width="60%" />
      <SkeletonLine $width="85%" />
    </DrawerBody>
  );
}

/** «8 682,03 ₽/маш.-ч с 01.11.2026» — что действует на дату экрана. */
function currentText(row: RateRow): string {
  if (row.state.kind === 'missing') return 'ставки на дату нет';
  const since = row.state.validFrom ? ` с ${formatIsoDate(row.state.validFrom)}` : '';
  return `${formatMoney(row.state.rate)} ${rateUnit(row.unit)}${since}`;
}

interface RateDrawerProps {
  target: RateTarget;
  groupId: string;
  /** Строка таблицы; `undefined` — таблица ещё грузится или группы нет. */
  row: RateRow | undefined;
  rowsLoading: boolean;
  /** Дата экрана. */
  on: string;
  canWrite: boolean;
  view: RateView;
  onView: (view: RateView) => void;
  onClose: () => void;
  /** Перечитать ставки на дату (после 409). */
  onRefreshRows: () => void;
}

/**
 * Ставка группы у объекта или по умолчанию: задать новую (форма с подсказкой)
 * и история прежних. Читателю — только история.
 */
export function RateDrawer({
  target,
  groupId,
  row,
  rowsLoading,
  on,
  canWrite,
  view,
  onView,
  onClose,
  onRefreshRows,
}: RateDrawerProps) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const dirtyRef = useRef(false);
  const [session, setSession] = useState(0);
  const [saved, setSaved] = useState<GroupRate | null>(null);

  const historyQ = useListGroupRatesQuery({
    project_id: targetKey(target),
    group_id: groupId,
    history: true,
  });
  const history = historyQ.data?.items;
  const tab: RateView = canWrite ? view : 'history';

  const onDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const confirmDiscard = useCallback(
    () =>
      !dirtyRef.current || window.confirm('Закрыть без сохранения? Новая ставка не сохранится.'),
    [],
  );

  const requestClose = useCallback(() => {
    if (!confirmDiscard()) return;
    dirtyRef.current = false;
    onClose();
  }, [confirmDiscard, onClose]);

  function changeTab(next: RateView) {
    if (next === tab) return;
    if (tab === 'new' && !confirmDiscard()) return;
    dirtyRef.current = false;
    if (next === 'new') setSession((s) => s + 1);
    onView(next);
  }

  function refresh() {
    void historyQ.refetch();
    onRefreshRows();
  }

  function onSaved(rate: GroupRate) {
    dirtyRef.current = false;
    setSaved(rate);
    onView('history');
    requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }));
  }

  const tabs: TabItem<RateView>[] = [
    { id: 'new', label: 'Новая ставка' },
    { id: 'history', label: 'История', count: history?.length },
  ];
  const groupHref = `/agents/econ/fleet?tab=groups&group=${encodeURIComponent(groupId)}`;
  const savedFuture = saved !== null && saved.valid_from > on;

  return (
    <Drawer open onClose={requestClose} labelledBy={`${uid}-title`} initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderTop>
          <Eyebrow>Ставка группы · {targetLabel(target)}</Eyebrow>
          <DrawerCloseButton onClick={requestClose} />
        </HeaderTop>
        <Title id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
          {row?.groupName ?? 'Группа механизаторов'}
        </Title>
        <MetaRow $last={!canWrite || !row}>
          {row && (
            <>
              <RateKindChip kind={row.state.kind} />
              <span className="text">
                на {formatIsoDate(on)}: {currentText(row)}
              </span>
              <GroupUnitChip unit={row.unit} />
            </>
          )}
        </MetaRow>
        {canWrite && row && (
          <Tabs
            items={tabs}
            value={tab}
            onChange={changeTab}
            idPrefix={uid}
            label="Ставка группы"
          />
        )}
      </DrawerHeader>

      {!row ? (
        rowsLoading ? (
          <Skeleton />
        ) : (
          <DrawerBody>
            <Notice $tone="danger" role="alert">
              <span className="text">
                {typo(
                  'Группы нет среди действующих — возможно, её отправили в архив или ссылка устарела.',
                )}
              </span>
              <TextButton type="button" onClick={onClose}>
                К списку
              </TextButton>
            </Notice>
          </DrawerBody>
        )
      ) : tab === 'new' ? (
        historyQ.isLoading ? (
          <Skeleton />
        ) : (
          <FormTab role="tabpanel" id={tabPanelId(uid, 'new')} aria-labelledby={tabId(uid, 'new')}>
            <RateForm
              key={session}
              target={target}
              row={row}
              on={on}
              open={openRate(history ?? [])}
              formId={`${uid}-form`}
              groupHref={groupHref}
              onCancel={requestClose}
              onSaved={onSaved}
              onRefresh={refresh}
              onDirtyChange={onDirtyChange}
            />
          </FormTab>
        )
      ) : (
        <DrawerBody>
          <MaybeTabPanel tabbed={canWrite} idPrefix={uid}>
            {saved && (
              <Flash $tone="success" role="status">
                <span className="text">
                  <strong>
                    Ставка {formatMoney(saved.rate)} {rateUnit(row.unit)} сохранена, действует с{' '}
                    {formatIsoDate(saved.valid_from)}.
                  </strong>{' '}
                  {savedFuture
                    ? typo(`На выбранную дату ${formatIsoDate(on)} пока действует прежняя ставка.`)
                    : ''}
                </span>
              </Flash>
            )}
            <RateHistory
              history={history}
              loading={historyQ.isLoading}
              error={
                historyQ.isError
                  ? apiErrorText(historyQ.error, 'Не удалось загрузить историю ставок.')
                  : null
              }
              onRetry={() => void historyQ.refetch()}
              on={on}
              target={target}
              fallback={row.fallback}
              unit={row.unit}
              savedId={saved?.id ?? null}
            />
          </MaybeTabPanel>
        </DrawerBody>
      )}
    </Drawer>
  );
}
